import { getRepresentatives, istPlanungsVerantwortlicher } from '../../services/projectRepresentatives';
import type { ProjectRepresentative, ReportMetadata } from '../../types';
import type { NotrufKontakt, NotrufplanInhalt } from '../sige-plan/types';
import type { Beteiligter } from './types';

/**
 * Uebernahme aus dem Bestand — Vertreterliste und Notrufplan.
 *
 * ═══ ZWEI RICHTUNGEN, ZWEI REGELN ═════════════════════════════════════════
 * Der Plan (`docs/asplan-modul-plan.md`, Abschnitt 10) unterscheidet scharf:
 *
 *   Vertreterliste → `asplan_beteiligte`   KOPIEREN, mit `quelle = 'projekt'`.
 *       Der A+S-Plan wird gedruckt, freigegeben und eingefroren. Ein Anhang 3
 *       Nr. 1, der beim Drucken nachschlaegt, druckte in zwei Revisionen
 *       verschiedene Namen — der Plan behauptet aber einen STAND.
 *
 *   Notrufplan → nur LESEN, „EINE Quelle".
 *       Klinik, Durchgangsarzt und Ersthelfer gehoeren dem SiGe-Plan. Wer sie
 *       in die asplan-Tabellen kopiert, hat sie ab dem naechsten Wechsel des
 *       Durchgangsarztes an zwei Stellen zu pflegen — und pflegt sie an
 *       einer. Der Notrufplan ist eine Notfallnummer: eine veraltete Kopie
 *       davon ist schlimmer als gar keine.
 *
 * ═══ WARUM NICHTS DAVON BEIM LADEN PASSIERT ═══════════════════════════════
 * Die Uebernahme laeuft ausschliesslich auf Knopfdruck. Eine Vorbelegung, die
 * sich selbst speichert, mutiert Bestandsdatensaetze rueckwirkend — das ist
 * die Lehre aus den Modulen Handbuch und Planungsphase und der Grund, warum
 * `fetchAsplanBundle` schon heute nichts anlegt.
 *
 * ═══ DER SCHLUESSEL DER WIEDERERKENNUNG ═══════════════════════════════════
 * `asplan_beteiligte` hat keine Spalte fuer die Herkunfts-Id (Migration Nr. 41
 * ist auf TEST und PROD gelaufen, eine neue Spalte waere eine neue Migration).
 * Wiedererkannt wird deshalb ueber `funktion_key` + normalisierten Personen-
 * namen. Das traegt, weil beide Seiten denselben Menschen benennen; es traegt
 * NICHT ueber eine Umbenennung hinweg — und genau deshalb wird eine nicht
 * mehr wiedergefundene Zeile ausgewiesen und nie geloescht.
 */

// ═══════════════════════════════════════════════════════════════════════════
// TEIL 1 — VERTRETERLISTE → BETEILIGTE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Vertreterliste des Projekts ist die Liste der SiGeKo nach BaustellV —
 * NICHT der Koordinator nach DGUV Regel 101-004. Das sind zwei Bestellungen,
 * und wer die eine als die andere eintraegt, behauptet eine Bestellung, die
 * es nicht gibt. Der DGUV-Koordinator bleibt eine Zeile von Hand.
 */
export const VERTRETER_FUNKTION_KEY = 'sigeko';
const FUNKTION_HAUPT = 'SiGeKo (BaustellV)';
const FUNKTION_VERTRETUNG = 'SiGeKo (BaustellV), Vertretung';
/**
 * Verantwortliche Planungsphase (docs/projekt-kickoff-plan.md §5): bestellt
 * fuer die Planung, nicht als Vertretung des Koordinators auf der Baustelle.
 * Gleicher `funktion_key` wie die uebrigen SiGeKo-Zeilen — so erkennt die
 * Uebernahme eine frueher als „Vertretung" uebernommene Zeile derselben Person
 * wieder und zieht nur die Bezeichnung nach, statt eine zweite anzulegen.
 */
const FUNKTION_PLANUNG = 'SiGeKo (BaustellV), Planung';

/**
 * Bis 11/2026 trugen die beiden Bezeichnungen oben einen Gedankenstrich statt
 * des Kommas. Uebernommene Bestandszeilen tragen ihn noch in `funktion`; sie
 * gelten als gleich und loesen keinen Nachzug aus. Sonst stuende in jedem
 * bestehenden Plan „wird nachgezogen (funktion)“, ohne dass sich an der
 * Vertreterliste etwas geaendert haette.
 */
const funktionVergleich = (s: string | null | undefined): string =>
    String(s ?? '').replace(/\s+[–—]\s+/g, ', ').trim();

/**
 * Die Funktion, unter der Bauherr und Auftraggeber gefuehrt werden.
 *
 * Anhang 3 Nr. 1 der DGUV Regel 101-004 nennt genau den „Auftraggeber" —
 * `bauherrenvertretung` ist eine andere Partei (die BEVOLLMAECHTIGTE) und
 * wird deshalb hier nicht verwendet.
 */
export const AUFTRAGGEBER_FUNKTION_KEY = 'auftraggeber';

/** Was aus einem Vertreter wird. Ohne Id: der Datensatz existiert noch nicht. */
export interface BeteiligtenVorlage {
    funktion_key: string;
    funktion: string;
    organisation: string;
    person: string;
    anschrift: string;
    telefon: string;
    mobil: string;
    email: string;
}

export interface UebernahmeAenderung {
    id: string;
    person: string;
    aenderung: Partial<Beteiligter>;
}

export interface UebernahmePlan {
    /** Wird angelegt (`quelle = 'projekt'`). */
    neu: BeteiligtenVorlage[];
    /** Wird nachgezogen — nur Zeilen mit `quelle = 'projekt'`. */
    aktualisieren: UebernahmeAenderung[];
    /** Stimmt bereits ueberein. */
    unveraendert: BeteiligtenVorlage[];
    /**
     * Steht schon von Hand drin. Wird WEDER angelegt (sonst zwei Zeilen fuer
     * denselben Menschen) NOCH angefasst (Regel: manuell bleibt manuell).
     */
    bereitsManuell: Beteiligter[];
    /**
     * Frueher uebernommen, heute nicht mehr in der Vertreterliste. Wird NICHT
     * geloescht: in so einer Zeile kann eine von Hand ergaenzte Anschrift oder
     * Telefonnummer stehen, und ein freigegebener Plan darf ohnehin nicht
     * hinter dem Ruecken seines Verfassers Beteiligte verlieren. Sie wird
     * ausgewiesen, damit jemand entscheidet.
     */
    entfallen: Beteiligter[];
}

/**
 * Vergleichsform eines Namens: Gross-/Kleinschreibung, Satzzeichen und
 * doppelte Leerzeichen sollen keinen zweiten Beteiligten erzeugen.
 * Umlaute bleiben stehen — „Mueller" und „Müller" sind zwei Schreibweisen
 * desselben Namens, aber das zu behaupten waere geraten.
 */
const nameNormal = (wert: string): string =>
    (wert || '')
        .toLowerCase()
        .replace(/[^a-z0-9äöüß]+/g, ' ')
        .trim();

/**
 * Funktionen, deren Beteiligter eine EINRICHTUNG ist und kein Mensch.
 *
 * Sie werden an der Organisation wiedererkannt, alle uebrigen an der Person.
 * Der Unterschied ist keine Formsache: Beim Auftraggeber wechselt der
 * Ansprechpartner und die Firma bleibt — wer ihn am Ansprechpartner
 * wiedererkennt, legt beim naechsten Personalwechsel eine zweite
 * Auftraggeberzeile an. Beim Koordinator ist es genau umgekehrt: der Mensch
 * ist bestellt, nicht seine Firma.
 */
const ORGANISATION_FUNKTIONEN = new Set([
    'auftraggeber', 'bauherrenvertretung', 'arbeitsschutzbehoerde',
    'gutachter', 'messstelle', 'geologe', 'bg',
]);

/**
 * Der Schluessel der Wiedererkennung.
 *
 * Das leitende Feld haengt an der Funktion (siehe oben); ist es leer, gilt
 * das jeweils andere. Damit bleibt eine Vertreterzeile auch dann erkennbar,
 * wenn jemand von Hand eine Organisation nachgetragen hat.
 */
export const beteiligtenSchluessel = (
    funktion_key: string, person: string, organisation = '',
): string => {
    const kopf = (funktion_key || '').trim().toLowerCase();
    const name = ORGANISATION_FUNKTIONEN.has(kopf)
        ? nameNormal(organisation) || nameNormal(person)
        : nameNormal(person) || nameNormal(organisation);
    return `${kopf}|${name}`;
};

const schluesselVonZeile = (b: Beteiligter): string =>
    beteiligtenSchluessel(b.funktion_key, b.person, b.organisation);

const schluesselVonVorlage = (v: BeteiligtenVorlage): string =>
    beteiligtenSchluessel(v.funktion_key, v.person, v.organisation);

/**
 * Bildet die Vertreterliste auf Beteiligten-Vorlagen ab.
 *
 * Erwartet die Liste so, wie `getRepresentatives(metadata)` sie liefert:
 * Index 0 ist der Hauptverantwortliche, alle weiteren sind Vertretungen —
 * ausser den Verantwortlichen Planungsphase (`bereich: 'planung'`), die als
 * SiGeKo Planung gefuehrt werden. Die Ableitung aus den Legacy-Feldern
 * `coordinator*` steckt dort und wird hier ausdruecklich NICHT nachgebaut.
 */
export const vertreterAlsVorlagen = (
    vertreter: ProjectRepresentative[] | null | undefined,
): BeteiligtenVorlage[] => {
    const roh: BeteiligtenVorlage[] = (vertreter || []).map((v, i) => ({
        funktion_key: VERTRETER_FUNKTION_KEY,
        // Position 0 bleibt der Koordinator, auch mit Bereich 'planung'.
        funktion: i === 0 ? FUNKTION_HAUPT : istPlanungsVerantwortlicher(v) ? FUNKTION_PLANUNG : FUNKTION_VERTRETUNG,
        // Die Firma des Koordinators steht nicht in `metadata` — sie ist die
        // eigene Firma und kommt aus dem Profil. Geraten wird sie nicht
        // (dieselbe Ueberlegung wie in `handbuchIntegration.ts`).
        organisation: '',
        person: (v.name || '').trim(),
        anschrift: '',
        telefon: '',
        mobil: (v.phone || '').trim(),
        email: (v.email || '').trim(),
    }));

    // Ohne Namen keine Zeile: ein Beteiligter ohne Person ist im Anhang 3
    // Nr. 1 kein Eintrag, sondern eine Luecke mit Rahmen.
    const benannt = roh.filter(v => v.person.length > 0);

    // Derselbe Kollege auf zwei Positionen ergaebe zwei Zeilen mit demselben
    // Schluessel — und damit eine Uebernahme, die sich selbst nicht mehr
    // wiedererkennt. Die vordere Position gewinnt.
    return benannt.filter((v, i) => benannt.findIndex(
        x => schluesselVonVorlage(x) === schluesselVonVorlage(v)) === i);
};

/**
 * Bauherr und Auftraggeber aus den Projektstammdaten.
 *
 * ═══ WARUM DAS EINE BETEILIGTENZEILE IST UND KEIN UNTERNEHMEN ═════════════
 * `asplan_unternehmen` traegt Aufsichtsfuehrenden, Sachkundenachweis nach
 * TRGS 524, Verpflichtungserklaerung, Arbeitsmediziner und die Anzeige an die
 * Berufsgenossenschaft. Das sind die Pflichten der AUSFUEHRENDEN Firmen im
 * kontaminierten Bereich. Ein Bauherr legt keinen Sachkundenachweis vor —
 * seine Zeile stuende dort dauerhaft auf „offen" und liefe in die
 * Ruecklaufkontrolle der Verpflichtungserklaerungen. Dazu kennt
 * `asplan_unternehmen` keine Spalte `quelle`; die Regel „uebernommen wird
 * nachgezogen, manuell nie" waere dort gar nicht abbildbar.
 *
 * Anhang 3 Nr. 1 nennt den Auftraggeber ausdruecklich als BETEILIGTEN — an
 * derselben Stelle wie Behoerde, Gutachter und Koordinator. Genau dorthin.
 *
 * ═══ ZWEI FELDER, EINE ODER ZWEI ZEILEN ═══════════════════════════════════
 * `metadata.clientName` ist der Bauherr, `metadata.customerName` der
 * Auftraggeber (Repo-Konvention, siehe `handbuchIntegration.ts`). Sind beide
 * dieselbe Firma — der Regelfall, im Anlagedialog gibt es dafuer den Knopf
 * „Wie Bauherr" — entsteht EINE Zeile mit beiden Funktionen im Text. Zwei
 * Zeilen mit demselben Firmennamen laesen sich wie zwei Vertragspartner.
 */
export const auftraggeberAlsVorlagen = (
    metadata: Partial<ReportMetadata> | null | undefined,
): BeteiligtenVorlage[] => {
    const m = metadata || {};
    const bauherr = (m.clientName || '').trim();
    const auftraggeber = (m.customerName || '').trim();

    const zeile = (
        funktion: string, name: string,
        person: string, anschrift: string, telefon: string, email: string,
    ): BeteiligtenVorlage => ({
        funktion_key: AUFTRAGGEBER_FUNKTION_KEY,
        funktion,
        organisation: name,
        person: (person || '').trim(),
        anschrift: (anschrift || '').trim(),
        telefon: (telefon || '').trim(),
        mobil: '',
        email: (email || '').trim(),
    });

    if (bauherr && nameNormal(bauherr) === nameNormal(auftraggeber)) {
        return [zeile('Bauherr und Auftraggeber', bauherr,
            m.clientContactPerson || '', m.clientAddress || '',
            m.clientPhone || '', m.clientEmail || '')];
    }

    const liste: BeteiligtenVorlage[] = [];
    if (bauherr) {
        liste.push(zeile('Bauherr', bauherr,
            m.clientContactPerson || '', m.clientAddress || '',
            m.clientPhone || '', m.clientEmail || ''));
    }
    if (auftraggeber) {
        liste.push(zeile('Auftraggeber', auftraggeber,
            m.customerContactPerson || '', m.customerAddress || '',
            m.customerPhone || '', m.customerEmail || ''));
    }
    return liste;
};

/**
 * Alles, was das Projekt an Beteiligten hergibt.
 *
 * `getRepresentatives` ist die eine Ableitung der Vertreterliste im Repo und
 * kennt auch die Legacy-Felder `coordinator*` der Alt-Projekte — sie wird
 * benutzt und nicht nachgebaut.
 */
export const vorlagenAusProjekt = (
    metadata: Partial<ReportMetadata> | null | undefined,
): BeteiligtenVorlage[] => [
    ...vertreterAlsVorlagen(getRepresentatives(metadata as ReportMetadata | undefined)),
    ...auftraggeberAlsVorlagen(metadata),
];

/**
 * Was eine uebernommene Zeile nachzieht.
 *
 * NUR gefuellte Werte aus der Quelle und nur, wenn sie abweichen. Ein leeres
 * Feld in der Quelle loescht nie eine von Hand ergaenzte Nummer: „dort steht
 * nichts" ist keine Aussage darueber, dass es hier nichts geben darf. Die
 * Vertreterliste kennt `organisation`, `anschrift` und `telefon` gar nicht —
 * sie bleiben bei Vertreterzeilen deshalb von selbst unberuehrt.
 */
const nachzug = (ziel: Beteiligter, v: BeteiligtenVorlage): Partial<Beteiligter> => {
    const u: Partial<Beteiligter> = {};
    if (v.funktion && funktionVergleich(v.funktion) !== funktionVergleich(ziel.funktion)) u.funktion = v.funktion;
    if (v.organisation && v.organisation !== ziel.organisation) u.organisation = v.organisation;
    if (v.person && v.person !== ziel.person) u.person = v.person;
    if (v.anschrift && v.anschrift !== ziel.anschrift) u.anschrift = v.anschrift;
    if (v.telefon && v.telefon !== ziel.telefon) u.telefon = v.telefon;
    if (v.mobil && v.mobil !== ziel.mobil) u.mobil = v.mobil;
    if (v.email && v.email !== ziel.email) u.email = v.email;
    return u;
};

/**
 * Rechnet aus, was eine Uebernahme taete — ohne sie zu tun.
 *
 * Diese Trennung ist der ganze Sinn der Funktion: der Anwender sieht vorher,
 * was passiert, und die Regel „manuell wird nie ueberschrieben" ist ohne
 * Datenbank pruefbar.
 */
export const planeUebernahme = (
    metadata: Partial<ReportMetadata> | null | undefined,
    bestand: Beteiligter[] | null | undefined,
): UebernahmePlan => {
    const vorlagen = vorlagenAusProjekt(metadata);
    const zeilen = bestand || [];

    const plan: UebernahmePlan = {
        neu: [], aktualisieren: [], unveraendert: [], bereitsManuell: [], entfallen: [],
    };

    for (const v of vorlagen) {
        const key = schluesselVonVorlage(v);
        const uebernommen = zeilen.find(z => z.quelle === 'projekt' && schluesselVonZeile(z) === key);

        if (uebernommen) {
            const u = nachzug(uebernommen, v);
            if (Object.keys(u).length > 0) {
                plan.aktualisieren.push({ id: uebernommen.id, person: uebernommen.person, aenderung: u });
            } else {
                plan.unveraendert.push(v);
            }
            continue;
        }

        const vonHand = zeilen.find(z => z.quelle === 'manuell' && schluesselVonZeile(z) === key);
        if (vonHand) {
            plan.bereitsManuell.push(vonHand);
            continue;
        }

        plan.neu.push(v);
    }

    const bekannt = new Set(vorlagen.map(schluesselVonVorlage));
    plan.entfallen = zeilen.filter(
        z => z.quelle === 'projekt' && !bekannt.has(schluesselVonZeile(z)));

    return plan;
};

/** Ob die Uebernahme ueberhaupt etwas taete. */
export const uebernahmeAendertEtwas = (plan: UebernahmePlan): boolean =>
    plan.neu.length > 0 || plan.aktualisieren.length > 0;

// ═══════════════════════════════════════════════════════════════════════════
// TEIL 2 — NOTRUFPLAN DES SIGE-PLANS (nur lesen)
// ═══════════════════════════════════════════════════════════════════════════

export interface NotfallStelle {
    /** 'krankenhaus' | 'durchgangsarzt' | 'augenarzt' | 'apotheke' */
    key: string;
    was: string;
    name: string;
    adresse: string;
    telefon: string;
    /** Bereits als Text: „3,2 km" oder leer. */
    entfernung: string;
    /** Von einem Menschen bestaetigt (`notfall_stellen`), nicht nur gefunden. */
    geprueft: boolean;
    hinweis: string;
}

const STELLEN_ORDNUNG: { key: keyof NotrufplanInhalt; label: string }[] = [
    { key: 'krankenhaus', label: 'Krankenhaus' },
    { key: 'durchgangsarzt', label: 'Durchgangsarzt' },
    { key: 'augenarzt', label: 'Augenarzt' },
    { key: 'apotheke', label: 'Apotheke' },
];

const entfernungText = (km: number | undefined): string =>
    typeof km === 'number' && isFinite(km) ? `${km.toFixed(1).replace('.', ',')} km` : '';

/**
 * Die Notfallstellen des Notrufplans als Anzeigezeilen.
 *
 * Eine Stelle ohne Namen UND ohne Telefonnummer ist kein Eintrag, sondern ein
 * angefangenes Formular — sie faellt raus. Ein halber Eintrag auf einem
 * Notfallblatt liest sich wie eine geklaerte Sache.
 */
export const notfallStellen = (
    inhalt: NotrufplanInhalt | null | undefined,
): NotfallStelle[] => {
    if (!inhalt) return [];

    const zeilen: NotfallStelle[] = [];
    for (const { key, label } of STELLEN_ORDNUNG) {
        const k = inhalt[key] as NotrufKontakt | undefined;
        if (!k) continue;
        const name = (k.name || '').trim();
        const telefon = (k.telefon || '').trim();
        if (!name && !telefon) continue;
        zeilen.push({
            key: String(key),
            was: label,
            name,
            adresse: (k.adresse || '').trim(),
            telefon,
            entfernung: entfernungText(k.entfernung),
            geprueft: Boolean((k.geprueftVon || '').trim()),
            hinweis: (k.hinweis || '').trim(),
        });
    }
    return zeilen;
};

export interface ErsthelferZeile {
    name: string;
    firma: string;
}

/** Die Ersthelfer des Notrufplans. Namenlose Zeilen fallen raus. */
export const ersthelfer = (
    inhalt: NotrufplanInhalt | null | undefined,
): ErsthelferZeile[] =>
    (inhalt?.ersthelfer || [])
        .map(e => ({ name: (e?.name || '').trim(), firma: (e?.firma || '').trim() }))
        .filter(e => e.name.length > 0);

// ═══════════════════════════════════════════════════════════════════════════
// TEIL 3 — DIE NOTFALLANGABEN DES PLANS (`asplaene.notfall`)
// ═══════════════════════════════════════════════════════════════════════════
//
// ═══ WARUM ES DIESEN KATALOG GIBT ══════════════════════════════════════════
// `asplaene.notfall` ist eine freie jsonb-Spalte. Genau deshalb wurde sie
// monatelang GELESEN (Notfall-Aushang, Notfallausweis) und von keinem Editor
// GESCHRIEBEN — der Aushang druckte in jedem Plan „—". Ein Notfallblatt mit
// Gedankenstrich statt Nummer ist schlimmer als keines: es sieht aus wie eine
// geprueft-leere Angabe. Der Katalog macht aus der freien Spalte eine Liste
// mit Namen, damit Editor, Luecken-Ausweis und Aushang dieselbe kennen.
//
// Die Schluessel sind snake_case ohne Umlaute und stehen woertlich so im
// jsonb; die Beschriftungen tragen echte Umlaute.

export interface NotfallFeld {
    key: string;
    label: string;
    hinweis?: string;
    mehrzeilig?: boolean;
    /**
     * Steht auf dem Aushang „Verhalten im Notfall" bzw. dem Notfallausweis.
     * Nur diese Felder erzeugen eine Luecke — die uebrigen sind Angaben zu
     * Kap. 10/12 des Plans und keine Nummer an der Wand.
     */
    aushang: boolean;
    /** Der Satz, der bei leerem Feld ausgewiesen wird. */
    luecke?: string;
}

export const NOTFALL_FELDER: NotfallFeld[] = [
    {
        key: 'giftnotruf', label: 'Giftnotruf', aushang: true,
        hinweis: 'Erscheint auf dem Notfall-Aushang und auf dem Notfallausweis.',
        luecke: 'Die Nummer der Giftnotrufzentrale fehlt.',
    },
    {
        key: 'koordinator_tel', label: 'Koordinator (Telefon)', aushang: true,
        hinweis: 'Der Koordinator nach DGUV Regel 101-004, im Notfall die erste Nummer nach 112.',
        luecke: 'Die Nummer des Koordinators fehlt.',
    },
    {
        key: 'bauleitung_tel', label: 'Bauleitung (Telefon)', aushang: true,
        luecke: 'Die Nummer der Bauleitung fehlt.',
    },
    {
        key: 'sammelplatz', label: 'Sammelplatz', aushang: true,
        hinweis: 'So beschreiben, dass ein Ortsfremder ihn findet: der Rettungsdienst wird dort abgeholt.',
        luecke: 'Der Sammelplatz ist nicht festgelegt.',
    },
    {
        key: 'zufahrt_breite', label: 'Rettungszufahrt', aushang: false,
        hinweis: 'Breite, Tragfähigkeit und Erreichbarkeit (Kap. 10).',
    },
    {
        key: 'ersthelfer_quote', label: 'Ersthelfer', aushang: false,
        hinweis: '§ 26 Abs. 1 DGUV Vorschrift 1: bei 2 bis 20 anwesenden Versicherten ein '
            + 'Ersthelfer, bei mehr als 20 anwesenden Versicherten 10 % der Anwesenden '
            + '(die 5 % gelten nur in Verwaltungs- und Handelsbetrieben). Maßgeblich sind '
            + 'die auf der Baustelle Anwesenden, nicht die Beschäftigten des Unternehmens: '
            + 'bei 8 Anwesenden ist ein Ersthelfer Pflicht, also 12,5 %.',
    },
    {
        key: 'loescher_standorte', label: 'Standorte der Feuerlöscher', aushang: false,
        mehrzeilig: true,
        hinweis: 'Kap. 12, Brandschutz. Auch die Löschmittel, die bei diesen Stoffen NICHT eingesetzt werden dürfen.',
    },
];

/**
 * Die Nummer der Giftnotrufzentrale — BELEGT, nicht erfunden.
 *
 * `030 19240` ist die Nummer, die dieses Repo an drei weiteren Stellen fest
 * fuehrt: `services/pdf/generatorAushang.ts`, `generatorHandbuch.ts` und
 * `components/handbuch/kapitel/Baustellenordnung.tsx` (dort mit dem Vermerk,
 * dass 112, 110 und der Giftnotruf ueberall dieselben sind). Sie wird hier
 * nur VORGESCHLAGEN — eingetragen wird sie auf Klick, damit ein Plan mit
 * abweichender regionaler Zentrale nicht stillschweigend ueberschrieben wird.
 */
export const GIFTNOTRUF = '030 19240';
const GIFTNOTRUF_QUELLE =
    'Giftnotruf Berlin, dieselbe Nummer wie im SiGe-Handbuch und auf dem Notrufplan-Aushang dieses Programms.';

export interface NotfallVorschlag {
    /** Schluessel aus `NOTFALL_FELDER`. */
    key: string;
    label: string;
    wert: string;
    /** Woher der Wert stammt. Steht in der Oberflaeche neben dem Vorschlag. */
    quelle: string;
}

/**
 * Was sich fuer die Notfallangaben aus dem Bestand ABLEITEN laesst.
 *
 * Ausdruecklich Vorschlaege und kein Vorbelegen: sie werden angezeigt, mit
 * ihrer Quelle daneben, und erst auf Klick eingetragen. Dieselbe Regel wie
 * bei den Beteiligten — eine Vorbelegung, die sich selbst speichert, mutiert
 * Bestandsdatensaetze rueckwirkend.
 *
 * Ein Vorschlag ohne belegten Wert entsteht nicht. Wo nichts herzuleiten ist,
 * bleibt das Feld leer und `notrufLuecken` weist es aus — geraten wird nichts.
 */
export const notfallVorschlaege = (
    metadata: Partial<ReportMetadata> | null | undefined,
): NotfallVorschlag[] => {
    const m = metadata || {};
    const liste: NotfallVorschlag[] = [
        { key: 'giftnotruf', label: 'Giftnotruf', wert: GIFTNOTRUF, quelle: GIFTNOTRUF_QUELLE },
    ];

    const sigeko = getRepresentatives(m as ReportMetadata | undefined)[0];
    const koordTel = (sigeko?.phone || '').trim();
    if (koordTel) {
        liste.push({
            key: 'koordinator_tel', label: 'Koordinator (Telefon)', wert: koordTel,
            quelle: `Vertreterliste des Projekts: ${sigeko.name}`,
        });
    }

    const bauleitTel = (m.siteContactPhone || '').trim();
    if (bauleitTel) {
        const wer = (m.siteContactPerson || m.siteContactCompany || '').trim();
        liste.push({
            key: 'bauleitung_tel', label: 'Bauleitung (Telefon)', wert: bauleitTel,
            quelle: wer
                ? `Bauleitung vor Ort aus den Projekt-Stammdaten: ${wer}`
                : 'Bauleitung vor Ort aus den Projekt-Stammdaten',
        });
    }

    return liste;
};

/** Der aktuelle Wert eines Notfallfeldes als Text. */
export const notfallWert = (
    notfall: Record<string, unknown> | null | undefined, key: string,
): string => String((notfall || {})[key] ?? '').trim();

/**
 * Warum das Notfallblatt heute unvollstaendig ist — als lesbare Saetze oder
 * leer, wenn nichts fehlt.
 *
 * Ausdruecklich KEIN Ersatzwert: die fehlende Nummer wird benannt, nicht
 * erfunden. Ein Aushang mit einem Gedankenstrich statt einer Nummer sieht aus
 * wie ein gepflegtes Blatt.
 */
export const notrufLuecken = (
    inhalt: NotrufplanInhalt | null | undefined,
    notfall: Record<string, unknown> | null | undefined,
): string[] => {
    const luecken: string[] = [];

    if (notfallStellen(inhalt).length === 0) {
        luecken.push('Im Notrufplan des SiGe-Plans ist keine Notfallstelle hinterlegt.');
    }
    for (const f of NOTFALL_FELDER) {
        if (!f.aushang || !f.luecke) continue;
        if (!notfallWert(notfall, f.key)) luecken.push(f.luecke);
    }

    return luecken;
};
